import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import SearchIcon from "@mui/icons-material/Search";
import { CiFilter } from "react-icons/ci";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import API_BASE_URL from "../../../../../../Constants";
import NavHeightAdjust from "../../../../../../components/navbar/NavHeightAdjust";
const BuildingLedgerFields = [
  { key: "transactionId", label: "Transaction ID" },
  { key: "transactionDate", label: "Date" },
  { key: "sourceDocument", label: "Source Document" },
  { key: "itemDescription", label: "Item Description" },
  { key: "debits", label: "Debits" },
  { key: "credits", label: "Credits" },
  { key: "balance", label: "Balance" },
];

const TransactionTypes = [
  { key: "selectAll", label: "Select All" },
  { key: "materials", label: "Materials" },
  { key: "labor", label: "Labor Costs" },
  { key: "freight", label: "Freight Charges" },
  { key: "misc", label: "Miscellaneous Charges" },
  { key: "salesTax", label: "Sales Tax" },
];

const ViewBuildingLedger = () => {
  // Obtain buildingId from search parameters
  const { id } = useParams();
  const buildingId = Number(id) || null;

  const [transactions, setTransactions] = useState([]);
  const [queriedTransactions, setQueriedTransactions] = useState([]);
  const [address, setAddress] = useState({});
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [creditTotal, setCreditTotal] = useState(0);
  const [debitTotal, setDebitTotal] = useState(0);

  const [allSelected, setAllSelected] = useState(true);
  const [selectedOptions, setSelectedOptions] = useState(
    TransactionTypes.map((type) => type.key)
  );
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    const fetchAddress = async (id) => {
      try {
        const response = await fetch(
          API_BASE_URL + `/api/setup/buildings/${id}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
            },
            credentials: "include",
          }
        );

        const data = await response.json();
        const addressId = data.addressID;

        if (addressId) {
          const addressResponse = await fetch(
            API_BASE_URL + `/api/setup/buildings/addresses/${addressId}`,
            {
              method: "GET",
              headers: {
                "Content-Type": "application/json",
              },
              credentials: "include",
            }
          );

          const addressData = await addressResponse.json();
          setAddress(addressData);
        }
      } catch (error) {
        console.error("Fetch error: ", error);
      }
    };

    const fetchTransactions = async (id) => {
      try {
        const response = await fetch(
          API_BASE_URL + `/api/setup/buildingledger/building/${id}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
            },
            credentials: "include",
          }
        );

        const data = await response.json();
        setTransactions(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error("Fetch error ", error);
      }
    };

    fetchAddress(buildingId);
    fetchTransactions(buildingId);
  }, []);

  useEffect(() => {
    // Handle the querying of transactions from 'filter by' menu and search bar
    const filterQuery = () => {
      setQueriedTransactions(
        transactions
          .sort(
            (
              t1,
              t2 // Sort transactions by date
            ) => (t1.transactionDate > t2.transactionDate ? 1 : -1)
          )
          .filter((transaction) => {
            // Search query
            const query = searchQuery.toLowerCase();
            return (
              (transaction.sourceDocument &&
                transaction.sourceDocument.toLowerCase().includes(query)) ||
              (transaction.itemDescription &&
                transaction.itemDescription.toLowerCase().includes(query))
            );
          })
          .filter((transaction) => {
            // Date query
            const date = new Date(transaction.transactionDate);
            return (
              (!fromDate || date >= new Date(fromDate)) &&
              (!toDate || date <= new Date(toDate))
            );
          })
          .filter(
            (
              transaction // Type query
            ) =>
              !allSelected ? selectedOptions.includes(transaction.type) : true
          )
      );
    };

    filterQuery();
  }, [transactions, toDate, fromDate, selectedOptions, searchQuery]);

  useEffect(() => {
    //Sum up the values in given column
    const sumColumn = (columnName) => {
      if (["credits", "debits"].includes(columnName)) {
        return queriedTransactions
          .reduce((acc, route) => acc + route[columnName], 0)
          .toFixed(2);
      }
    };

    setDebitTotal(sumColumn("debits"));
    setCreditTotal(sumColumn("credits"));
  }, [queriedTransactions]);

  // Handle filter options
  const handleTransactionTypeChange = (e) => {
    const { value, checked } = e.target;
    if (value === "selectAll" && checked) {
      setAllSelected(true);
      setSelectedOptions(TransactionTypes.map((type) => type.key));
    } else if (value === "selectAll") {
      setAllSelected(false);
      setSelectedOptions([]);
    } else if (checked) {
      setSelectedOptions((prev) => [...prev, value]);
    } else {
      setAllSelected(false);
      setSelectedOptions((prev) =>
        prev.filter((option) => option !== value && option !== "selectAll")
      );
    }
  };

  //Export to .xlsx file and download functionality
  const exportData = (data, filename) => {
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
    const wbout = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([wbout], { type: "application/octet-stream" }), filename);
  };

  const formatDollarAmount = (value) => {
    if (typeof value === "number") {
      return "$ " + value.toFixed(2);
    } else {
      return "$ 0.00";
    }
  };

  const formatDate = (date) => {
    const formattedDate = new Date(date).toLocaleDateString("en-US", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
    return formattedDate;
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleToDateChange = (e) => {
    setToDate(e.target.value);
  };

  const handleFromDateChange = (e) => {
    setFromDate(e.target.value);
  };

  const handleFilterOpenChange = () => {
    setIsFilterOpen(!isFilterOpen);
  };

  return (
    <NavHeightAdjust>
      <div className="mx-auto pl-4 pr-4 h-screen w-full">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold">Building Ledger</h2>
          <div className="flex justify-end items-center gap-4">
            <div className="flex">
              <label className="block mr-4 text-gray-700">Building Site</label>
              <input
                type="text"
                readOnly
                value={address.physicalStreet1}
                className="block w-full p-2 text-center bg-gray-300 border border-gray-300 rounded-md shadow-sm outline-none focus-within:ring-2 focus-within:ring-blue-500"
              />
            </div>
            <div className="flex items-center border border-gray-300 rounded-lg overflow-hidden">
              <div className="px-3 py-2 text-gray-500">
                <SearchIcon />
              </div>
              <input
                type="text"
                className="px-3 py-2 w-full outline-none"
                placeholder="Search"
                value={searchQuery}
                onChange={handleSearchChange}
              />
            </div>
            <div className="relative">
              <button
                onClick={handleFilterOpenChange}
                className="py-2 px-3 border-sss-blue-sel rounded-lg flex border text-sss-blue-sel font-bold
                            hover:bg-sss-blue hover:text-white transition duration-300 ease-in-out"
              >
                <CiFilter className="text-2xl" />
                Filter
              </button>
              {isFilterOpen && (
                <div className="p-2 absolute right-0 w-56 bg-white border border-gray-300 rounded-lg shadow-lg">
                  <div className="items-center">
                    <label className="block font-semibold text-gray-700">
                      From
                    </label>
                    <input
                      type="date"
                      value={fromDate}
                      onChange={handleFromDateChange}
                      className="outline-none block w-full p-2 border border-gray-300 rounded-md shadow-sm focus-within:ring-2 focus-within:ring-blue-500"
                    />
                  </div>
                  <div className="mt-1 items-center">
                    <label className="block font-semibold text-gray-700">
                      To
                    </label>
                    <input
                      type="date"
                      value={toDate}
                      onChange={handleToDateChange}
                      className="outline-none block w-full p-2 border border-gray-300 rounded-md shadow-sm focus-within:ring-2 focus-within:ring-blue-500"
                    />
                  </div>
                  <div className="mt-1 items-center">
                    <label className="font-semibold text-gray-700">
                      Transaction Type
                    </label>
                    {TransactionTypes.map((type) => (
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          value={type.key}
                          checked={selectedOptions.includes(type.key)}
                          onChange={handleTransactionTypeChange}
                          className="mr-2"
                        />
                        <label className="text-gray-700">{type.label}</label>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div>
              <button
                type="export"
                className="py-2 px-4 bg-blue-500 text-white font-semibold rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-400"
                onClick={() =>
                  exportData(queriedTransactions, "BuildingLedger.xlsx")
                }
              >
                Export Excel
              </button>
            </div>
          </div>
        </div>
        <table className="min-w-full bg-white rounded-lg shadow-lg overflow-hidden border-2 border-gray-200">
          <thead className="bg-gray-200">
            <tr>
              {BuildingLedgerFields.map((field) => (
                <td
                  key={field.key}
                  className="px-4 py-2 text-left font-bold text-sss-blue-sel"
                >
                  {field.label}
                </td>
              ))}
            </tr>
          </thead>
          <tbody className="text-gray-700">
            {queriedTransactions.map((transaction) => (
              <tr key={transaction.id} className="border-t">
                {BuildingLedgerFields.map((field) => (
                  <td key={field.key} className="px-4 py-2">
                    {transaction[field.key] !== null
                      ? field.key === "debits" || field.key === "credits"
                        ? formatDollarAmount(transaction[field.key])
                        : field.key === "transactionDate"
                        ? formatDate(transaction[field.key])
                        : transaction[field.key]
                      : ""}
                  </td>
                ))}
              </tr>
            ))}
            <tr className="border-t">
              {BuildingLedgerFields.map((field) => {
                switch (field.key) {
                  case "transactionId":
                    return <td className="px-4 py-2 font-semibold">Total</td>;
                  case "debits":
                    return (
                      <td key={field.key} className="px-4 py-2">
                        {"$ " + debitTotal}
                      </td>
                    );
                  case "credits":
                    return (
                      <td key={field.key} className="px-4 py-2">
                        {"$ " + creditTotal}
                      </td>
                    );
                  case "balance":
                    return (
                      <td key={field.key} className="px-4 py-2">
                        {"$ " + (debitTotal - creditTotal).toFixed(2)}
                      </td>
                    );
                  default:
                    return <td />;
                }
              })}
            </tr>
          </tbody>
        </table>
      </div>
    </NavHeightAdjust>
  );
};

export default ViewBuildingLedger;
